import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Link2, Sparkles, Unlink, Check, Loader2, CheckCircle2, AlertTriangle, Info, PartyPopper, Search, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';
import { useIsSuperAdmin } from '../stores/authStore';

type MonthSummary = {
  month: number;
  bookBalance: number;
  statementBalance: number | null;
  gap: number | null;
  status: string;
  reconciled: boolean;
  hasStatementBalance: boolean;
  discrepancyNote: string | null;
  bookCount: number;
  statementCount: number;
  matchedCount: number;
  matchedBookCount: number;
  matchedStatementCount: number;
};

type BookLine = {
  entryLineId: number;
  entryId: number | null;
  entryNumber: string | null;
  date: string | null;
  label: string;
  amount: number;
  matched: boolean;
};

type StatementLine = {
  id: number;
  operationDate: string;
  bankLabel: string;
  amount: number;
  matched: boolean;
  duplicate: boolean;
};

type Match = {
  id: number;
  entryLineIds: number[];
  statementLineIds: number[];
  entries: MatchLine[];
  statements: MatchLine[];
  aiGenerated: boolean;
  confirmed: boolean;
};

type MatchLine = {
  id: number;
  date: string | null;
  label: string;
  amount: number;
};

type MonthDetail = {
  bankAccount: string;
  year: number;
  month: number;
  bookBalance: number;
  statementBalance: number | null;
  suggestedStatementBalance: number | null;
  gap: number | null;
  status: string;
  discrepancyNote: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  bookLines: BookLine[];
  statementLines: StatementLine[];
  matches: Match[];
};

type BankAccount = { number: string; label: string };

type GapHypothesis = {
  cause: string;
  suspectedLines: string[];
  estimatedAmount: number | null;
  action: string;
};

type GapAnalysis = {
  summary: string;
  confidence: string;
  hypotheses: GapHypothesis[];
};

const monthName = (year: number, month: number) =>
  new Date(year, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long' });

const isClean = (gap: number | null) => gap != null && Math.abs(gap) < 0.01;

const BADGE = 'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium';
const badgeNeutral = `${BADGE} bg-surface-raised text-ink-muted`;
const badgeIncome = `${BADGE} bg-income-tint text-income`;
const badgeExpense = `${BADGE} bg-expense-tint text-expense`;
const badgeGold = `${BADGE} bg-gold-tint text-gold`;

/** Left-accent colour of a month card by its reconciliation state. */
const accentByTone: Record<'clean' | 'gap' | 'todo' | 'empty', string> = {
  clean: 'border-l-4 border-l-income',
  gap: 'border-l-4 border-l-expense',
  todo: 'border-l-4 border-l-gold',
  empty: 'border-l-4 border-l-line',
};

export default function Reconciliation() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isSuperAdmin = useIsSuperAdmin();
  const now = new Date();
  const [account, setAccount] = useState<string>('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(null);

  const accounts = useQuery<BankAccount[]>({
    queryKey: ['recon-accounts'],
    queryFn: () => api.get('/reconciliations/accounts').then((r) => r.data),
  });

  // Default to the first bank account once the list loads.
  useEffect(() => {
    if (!account && accounts.data && accounts.data.length > 0) {
      setAccount(accounts.data[0].number);
    }
  }, [accounts.data, account]);

  const summary = useQuery<MonthSummary[]>({
    queryKey: ['recon-year', account, year],
    queryFn: () => api.get('/reconciliations', { params: { account, year } }).then((r) => r.data),
    enabled: !!account,
  });

  const detail = useQuery<MonthDetail>({
    queryKey: ['recon-month', account, year, month],
    queryFn: () =>
      api.get('/reconciliations/month', { params: { account, year, month } }).then((r) => r.data),
    enabled: !!account && month != null,
  });

  const applyDetail = (data: MonthDetail) => {
    qc.setQueryData(['recon-month', account, year, month], data);
    qc.invalidateQueries({ queryKey: ['recon-year', account, year] });
  };

  const [statementBalance, setStatementBalance] = useState('');
  const [note, setNote] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [selectedBook, setSelectedBook] = useState<number[]>([]);
  const [selectedStatement, setSelectedStatement] = useState<number[]>([]);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const [autoConfirm, setAutoConfirm] = useState(true);

  // Statement-line search (client-side filtering of the open statement movements).
  const [sFrom, setSFrom] = useState('');
  const [sTo, setSTo] = useState('');
  const [sQuery, setSQuery] = useState('');
  const [sMin, setSMin] = useState('');
  const [sMax, setSMax] = useState('');
  const [stmtSearchOpen, setStmtSearchOpen] = useState(false);

  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  // Sync the editable balance/note fields whenever a month detail loads.
  useEffect(() => {
    if (detail.data) {
      const d = detail.data;
      const prefill =
        d.statementBalance != null
          ? d.statementBalance
          : d.suggestedStatementBalance;
      setStatementBalance(prefill != null ? String(prefill) : '');
      setNote(detail.data.discrepancyNote ?? '');
      setPeriodStart(d.periodStart ?? '');
      setPeriodEnd(d.periodEnd ?? '');
      setSelectedBook([]);
      setSelectedStatement([]);
    }
  }, [detail.data]);

  const saveBalance = useMutation({
    mutationFn: (completed: boolean) =>
      api
        .post('/reconciliations/month', {
          account,
          year,
          month,
          statementBalance: statementBalance === '' ? null : Number(statementBalance),
          discrepancyNote: note || null,
          completed,
          periodStart: periodStart || null,
          periodEnd: periodEnd || null,
        })
        .then((r) => r.data as MonthDetail),
    onSuccess: applyDetail,
  });

  const autoMatch = useMutation({
    mutationFn: () =>
      api.post('/reconciliations/auto-match', { account, year, month, autoConfirm }).then((r) => r.data as MonthDetail),
    onSuccess: applyDetail,
  });

  const addMatch = useMutation({
    mutationFn: () =>
      api
        .post('/reconciliations/match', {
          account,
          year,
          month,
          entryLineIds: selectedBook,
          statementLineIds: selectedStatement,
        })
        .then((r) => r.data as MonthDetail),
    onSuccess: (data) => {
      applyDetail(data);
      setSelectedBook([]);
      setSelectedStatement([]);
    },
  });

  const removeMatch = useMutation({
    mutationFn: (matchId: number) =>
      api
        .post('/reconciliations/match/remove', { account, year, month, matchId })
        .then((r) => r.data as MonthDetail),
    onSuccess: applyDetail,
  });

  const confirmMatch = useMutation({
    mutationFn: (v: { matchId: number; confirmed: boolean }) =>
      api
        .post('/reconciliations/match/confirm', { account, year, month, ...v })
        .then((r) => r.data as MonthDetail),
    onSuccess: applyDetail,
  });

  const deleteStatementLine = useMutation({
    mutationFn: (lineId: number) =>
      api
        .post('/reconciliations/statement-line/remove', { account, year, month, lineId })
        .then((r) => r.data as MonthDetail),
    onSuccess: applyDetail,
    onError: (e: any) =>
      window.alert(e?.response?.data?.message ?? t('reconciliation.deleteLineError')),
  });

  // Super-admin only: read-only AI explanation of the month's gap.
  const analyze = useMutation({
    mutationFn: () =>
      api.post('/reconciliations/analyze', { account, year, month }).then((r) => r.data as GapAnalysis),
  });

  // A fresh month selection clears any previous AI analysis.
  useEffect(() => {
    analyze.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, year, month]);

  const liveGap = useMemo(() => {
    if (!detail.data) return null;
    if (statementBalance === '') return null;
    return Number(statementBalance) - detail.data.bookBalance;
  }, [detail.data, statementBalance]);

  const d = detail.data;
  const bookOpen = d ? d.bookLines.filter((l) => !l.matched) : [];
  const stmtOpen = d ? d.statementLines.filter((l) => !l.matched) : [];
  const stmtFiltered = useMemo(() => {
    const q = sQuery.trim().toLowerCase();
    const min = sMin === '' ? null : Number(sMin);
    const max = sMax === '' ? null : Number(sMax);
    return stmtOpen.filter((l) => {
      if (sFrom && l.operationDate < sFrom) return false;
      if (sTo && l.operationDate > sTo) return false;
      if (q && !l.bankLabel.toLowerCase().includes(q)) return false;
      const abs = Math.abs(l.amount);
      if (min != null && abs < min) return false;
      if (max != null && abs > max) return false;
      return true;
    });
  }, [stmtOpen, sFrom, sTo, sQuery, sMin, sMax]);
  const hasStmtSearch = Boolean(sFrom || sTo || sQuery.trim() || sMin || sMax);
  const resetStmtSearch = () => {
    setSFrom('');
    setSTo('');
    setSQuery('');
    setSMin('');
    setSMax('');
  };
  // Everything is ticked off: no open book or statement movement remains, yet the month had activity.
  const allPointed = Boolean(
    d && bookOpen.length === 0 && stmtOpen.length === 0 && (d.bookLines.length > 0 || d.statementLines.length > 0),
  );
  const bookTotal = d ? d.bookLines.length : 0;
  const bookMatched = d ? d.bookLines.filter((l) => l.matched).length : 0;
  const pointPct = bookTotal ? Math.round((bookMatched / bookTotal) * 100) : 0;
  const selBookSum = d
    ? d.bookLines.filter((l) => selectedBook.includes(l.entryLineId)).reduce((s, l) => s + l.amount, 0)
    : 0;
  const selStmtSum = d
    ? d.statementLines.filter((l) => selectedStatement.includes(l.id)).reduce((s, l) => s + l.amount, 0)
    : 0;
  const selBalanced = Math.abs(selBookSum - selStmtSum) < 0.01;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold">{t('reconciliation.title')}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={account}
            onChange={(e) => {
              setAccount(e.target.value);
              setMonth(null);
            }}
            className="input !w-auto"
          >
            {(accounts.data ?? []).map((a) => (
              <option key={a.number} value={a.number}>
                {a.number} · {a.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <button className="btn btn-ghost !px-2" onClick={() => { setYear((y) => y - 1); setMonth(null); }} aria-label={t('reconciliation.prevYear')}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="tnum min-w-[3.5rem] text-center font-medium">{year}</span>
            <button className="btn btn-ghost !px-2" onClick={() => { setYear((y) => y + 1); setMonth(null); }} aria-label={t('reconciliation.nextYear')}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <p className="text-sm text-ink-muted">{t('reconciliation.intro')}</p>

      <div className="flex items-start gap-2 rounded-xl border border-brand/30 bg-brand-tint/40 p-3 text-sm text-ink-soft">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <span>{t('reconciliation.method')}</span>
      </div>

      {/* Year overview */}
      <div className="card p-5">
        {summary.isLoading ? (
          <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" />{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(summary.data ?? []).map((m) => {
              const active = m.month === month;
              const empty = m.bookCount === 0 && m.statementCount === 0 && !m.hasStatementBalance;
              const tone: 'clean' | 'gap' | 'todo' | 'empty' = m.hasStatementBalance
                ? (isClean(m.gap) ? 'clean' : 'gap')
                : empty
                  ? 'empty'
                  : 'todo';
              return (
                <button
                  key={m.month}
                  onClick={() => setMonth(m.month)}
                  className={`rounded-xl border p-3 text-left transition ${accentByTone[tone]} ${
                    active ? 'border-brand ring-1 ring-brand' : 'border-line hover:border-brand/50'
                  } ${empty ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize">{monthName(year, m.month)}</span>
                    {m.hasStatementBalance ? (
                      isClean(m.gap) ? (
                        <span className={badgeIncome}>{t('reconciliation.clean')}</span>
                      ) : (
                        <span className={badgeExpense}>{t('reconciliation.gap')}</span>
                      )
                    ) : (
                      <span className={badgeGold}>{t('reconciliation.todo')}</span>
                    )}
                  </div>
                  <div className="mt-2 space-y-0.5 text-xs text-ink-muted">
                    <div className="flex justify-between">
                      <span>{t('reconciliation.bookBalance')}</span>
                      <span className="tnum">{euro(m.bookBalance)}</span>
                    </div>
                    {m.hasStatementBalance && (
                      <>
                        <div className="flex justify-between">
                          <span>{t('reconciliation.statementBalance')}</span>
                          <span className="tnum">{euro(m.statementBalance)}</span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>{t('reconciliation.gapLabel')}</span>
                          <span className={`tnum ${isClean(m.gap) ? 'text-income' : 'text-expense'}`}>{euro(m.gap)}</span>
                        </div>
                      </>
                    )}
                    <div className="pt-1 text-[11px]">
                      {(() => {
                        const remBook = m.bookCount - m.matchedBookCount;
                        const remStmt = m.statementCount - m.matchedStatementCount;
                        const hasActivity = m.bookCount > 0 || m.statementCount > 0;
                        if (hasActivity && remBook <= 0 && remStmt <= 0) {
                          return (
                            <span className="inline-flex items-center gap-1 font-medium text-income">
                              <Check className="h-3 w-3" />
                              {t('reconciliation.allLinked')}
                              <span className="font-normal text-ink-muted">
                                · {t('reconciliation.counts', { book: m.bookCount, statement: m.statementCount })}
                              </span>
                            </span>
                          );
                        }
                        if (hasActivity) {
                          const parts = [
                            remBook > 0 ? t('reconciliation.nBook', { count: remBook }) : null,
                            remStmt > 0 ? t('reconciliation.nLines', { count: remStmt }) : null,
                          ].filter(Boolean);
                          return (
                            <span className="font-medium text-gold">
                              {t('reconciliation.toLink')} {parts.join(' · ')}
                            </span>
                          );
                        }
                        return (
                          <span className="text-ink-muted">
                            {t('reconciliation.counts', { book: m.bookCount, statement: m.statementCount })}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Month detail */}
      {month != null && d && (
        <div className="space-y-5">
          <div className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-semibold capitalize">
                {monthName(year, month)} {year}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
                  <input
                    type="checkbox"
                    checked={autoConfirm}
                    onChange={(e) => setAutoConfirm(e.target.checked)}
                    className="h-4 w-4 rounded border-line text-brand focus:ring-brand"
                  />
                  {t('reconciliation.autoConfirm')}
                </label>
                <button
                  className="btn btn-secondary"
                  onClick={() => autoMatch.mutate()}
                  disabled={autoMatch.isPending}
                >
                  {autoMatch.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {t('reconciliation.autoMatch')}
                </button>
              </div>
            </div>

            {/* Reconciliation status banner (AssoConnect-style: statement balance vs book balance) */}
            {liveGap == null ? (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-gold-tint px-3 py-2 text-sm font-medium text-gold">
                <Info className="h-4 w-4 shrink-0" />
                {t('reconciliation.pendingBanner')}
              </div>
            ) : isClean(liveGap) ? (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-income-tint px-3 py-2 text-sm font-medium text-income">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {t('reconciliation.reconciledBanner')}
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-expense-tint px-3 py-2 text-sm font-medium text-expense">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('reconciliation.gapBanner', { amount: euro(liveGap) })}
              </div>
            )}

            {/* Super-admin AI assistant: explain the gap (read-only), only when a saved gap exists. */}
            {isSuperAdmin && d.gap != null && !isClean(d.gap) && (
              <div className="mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => analyze.mutate()}
                  disabled={analyze.isPending}
                >
                  {analyze.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {t('reconciliation.assistantButton')}
                </button>
                {analyze.isError && (
                  <p className="mt-2 text-sm text-expense">
                    {(analyze.error as any)?.response?.data?.message ?? t('reconciliation.assistantError')}
                  </p>
                )}
                {analyze.data && (
                  <div className="reveal mt-3 rounded-xl border border-brand/30 bg-brand-tint/30 p-4">
                    <div className="mb-2 flex items-center gap-2 font-medium text-brand">
                      <Sparkles className="h-4 w-4 shrink-0" />
                      {t('reconciliation.assistantTitle')}
                      <span className="ml-auto text-xs font-normal text-ink-muted">
                        {t('reconciliation.assistantConfidence')} : {analyze.data.confidence}
                      </span>
                    </div>
                    <p className="text-sm text-ink-soft">{analyze.data.summary}</p>
                    {analyze.data.hypotheses.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {analyze.data.hypotheses.map((h, i) => (
                          <li key={i} className="rounded-lg border border-line bg-surface p-3">
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-medium text-ink">{h.cause}</span>
                              {h.estimatedAmount != null && (
                                <span className="tnum shrink-0 text-expense">{euro(h.estimatedAmount)}</span>
                              )}
                            </div>
                            {h.suspectedLines.length > 0 && (
                              <ul className="mt-1 list-disc pl-5 text-xs text-ink-muted">
                                {h.suspectedLines.map((s, j) => (
                                  <li key={j}>{s}</li>
                                ))}
                              </ul>
                            )}
                            {h.action && <p className="mt-1 text-xs text-brand">{h.action}</p>}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="mt-3 text-[11px] text-ink-muted">
                      {t('reconciliation.assistantDisclaimer')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pointing progress */}
            {bookTotal > 0 && (
              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between text-xs text-ink-muted">
                  <span>{t('reconciliation.progress', { matched: bookMatched, total: bookTotal })}</span>
                  <span className="tnum font-medium text-ink-soft">{pointPct}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-raised">
                  <div
                    className={`h-full rounded-full transition-all ${pointPct === 100 ? 'bg-income' : 'bg-brand'}`}
                    style={{ width: `${pointPct}%` }}
                  />
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.periodStart')}</span>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="input"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.periodEnd')}</span>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="input"
                />
              </label>
            </div>
            <p className="mt-1 text-xs text-ink-muted">{t('reconciliation.periodHint')}</p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.statementBalance')}</span>
                <input
                  type="number"
                  step="0.01"
                  value={statementBalance}
                  onChange={(e) => setStatementBalance(e.target.value)}
                  className="input tnum"
                  placeholder="0.00"
                />
                {d.statementBalance == null && d.suggestedStatementBalance != null && (
                  <span className="mt-1 block text-xs text-brand">
                    {t('reconciliation.fromImport')}
                  </span>
                )}
              </label>
              <div className="block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.bookBalance')}</span>
                <div className="input tnum flex items-center bg-surface-raised">{euro(d.bookBalance)}</div>
              </div>
              <div className="block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.gapLabel')}</span>
                <div className={`input tnum flex items-center font-medium ${liveGap == null ? '' : isClean(liveGap) ? 'text-income bg-income-tint' : 'text-expense bg-expense-tint'}`}>
                  {liveGap == null ? '-' : euro(liveGap)}
                </div>
              </div>
            </div>

            {liveGap != null && !isClean(liveGap) && (
              <label className="mt-4 block">
                <span className="mb-1 block text-sm text-ink-soft">{t('reconciliation.noteLabel')}</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="input"
                  placeholder={t('reconciliation.notePlaceholder')}
                />
              </label>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button className="btn btn-primary" onClick={() => saveBalance.mutate(false)} disabled={saveBalance.isPending}>
                {saveBalance.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('common.save')}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => saveBalance.mutate(true)}
                disabled={saveBalance.isPending || liveGap == null || (!isClean(liveGap) && !note.trim())}
                title={liveGap != null && !isClean(liveGap) && !note.trim() ? t('reconciliation.needNote') : ''}
              >
                <Check className="h-4 w-4" />
                {t('reconciliation.markDone')}
              </button>
              {d.status === 'COMPLETED' && (
                <span className={badgeIncome}>{t('reconciliation.completed')}</span>
              )}
            </div>
          </div>

          {/* Sticky selection bar: shows both sides' totals, green when balanced */}
          {(selectedBook.length > 0 || selectedStatement.length > 0) && (
            <div className="sticky top-2 z-20">
              <div
                className={`card flex flex-wrap items-center gap-4 p-4 shadow-lg ring-1 ${
                  selectedBook.length > 0 && selectedStatement.length > 0
                    ? selBalanced
                      ? 'bg-income-tint/40 ring-income'
                      : 'bg-expense-tint/30 ring-expense'
                    : 'ring-brand/40'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand" />
                  <div>
                    <div className="text-xs text-ink-muted">{t('reconciliation.bookEntries')}</div>
                    <div className="text-sm font-medium">
                      {t('reconciliation.selCount', { count: selectedBook.length })} ·{' '}
                      <span className="tnum">{euro(selBookSum)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex min-w-[3rem] flex-col items-center">
                  {selectedBook.length > 0 && selectedStatement.length > 0 ? (
                    selBalanced ? (
                      <span className="flex items-center gap-1 font-semibold text-income">
                        <CheckCircle2 className="h-4 w-4" />
                        {t('reconciliation.balanced')}
                      </span>
                    ) : (
                      <span className="tnum font-medium text-expense">
                        Δ {euro(selStmtSum - selBookSum)}
                      </span>
                    )
                  ) : (
                    <ChevronRight className="h-4 w-4 text-ink-muted" />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-gold" />
                  <div>
                    <div className="text-xs text-ink-muted">{t('reconciliation.statementLines')}</div>
                    <div className="text-sm font-medium">
                      {t('reconciliation.selCount', { count: selectedStatement.length })} ·{' '}
                      <span className="tnum">{euro(selStmtSum)}</span>
                    </div>
                  </div>
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setSelectedBook([]);
                      setSelectedStatement([]);
                    }}
                  >
                    {t('reconciliation.clearSelection')}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => addMatch.mutate()}
                    disabled={
                      selectedBook.length === 0 || selectedStatement.length === 0 || addMatch.isPending
                    }
                  >
                    {addMatch.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="h-4 w-4" />
                    )}
                    {t('reconciliation.link')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Everything ticked off: celebratory confirmation instead of empty tables */}
          {allPointed && (
            <div className="card border-l-4 border-l-income p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-income-tint">
                <PartyPopper className="h-7 w-7 text-income" />
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold text-ink">{t('reconciliation.allPointedTitle')}</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-soft">{t('reconciliation.allPointedBody')}</p>
            </div>
          )}

          {/* Two movement lists */}
          {!allPointed && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="card border-l-4 border-l-brand p-5">
              <h3 className="mb-3 flex items-center gap-2 font-medium">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand" />
                {t('reconciliation.bookEntries')}
                <span className={`ml-auto ${badgeNeutral}`}>{bookOpen.length}</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th>{t('field.date')}</th>
                      <th>{t('field.label')}</th>
                      <th className="text-right">{t('field.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookOpen.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-ink-muted">{t('reconciliation.noBookLines')}</td></tr>
                    )}
                    {bookOpen.map((l) => {
                      const sel = selectedBook.includes(l.entryLineId);
                      return (
                      <tr
                        key={l.entryLineId}
                        onClick={() => setSelectedBook((v) => toggle(v, l.entryLineId))}
                        className={`cursor-pointer ${sel ? 'bg-brand-tint ring-1 ring-inset ring-brand/50' : 'hover:bg-surface-raised'}`}
                      >
                        <td className="w-8">
                          <span className={`grid h-4 w-4 place-items-center rounded border ${sel ? 'border-brand bg-brand text-brand-contrast' : 'border-line'}`}>
                            {sel && <Check className="h-3 w-3" />}
                          </span>
                        </td>
                        <td data-label={t('field.date')} className="text-ink-soft">{dateFmt(l.date)}</td>
                        <td data-label={t('field.label')}>
                          {l.label}
                          {l.entryNumber && <span className="ml-1 text-xs text-ink-muted">{l.entryNumber}</span>}
                        </td>
                        <td data-label={t('field.amount')} className={`tnum text-right ${l.amount < 0 ? 'text-expense' : 'text-income'}`}>{euro(l.amount)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card border-l-4 border-l-gold p-5">
              <h3 className="mb-3 flex items-center gap-2 font-medium">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-gold" />
                {t('reconciliation.statementLines')}
                <button
                  type="button"
                  onClick={() => setStmtSearchOpen((v) => !v)}
                  className={`ml-auto inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${hasStmtSearch ? 'bg-brand-tint text-brand' : 'text-ink-muted hover:text-ink'}`}
                  aria-expanded={stmtSearchOpen}
                >
                  <Search className="h-3.5 w-3.5" />
                  {t('reconciliation.search')}
                </button>
                <span className={badgeNeutral}>{hasStmtSearch ? `${stmtFiltered.length}/${stmtOpen.length}` : stmtOpen.length}</span>
              </h3>
              {stmtSearchOpen && (
                <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl border border-line bg-surface-raised/50 p-3">
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">{t('reconciliation.searchFrom')}</span>
                    <input type="date" value={sFrom} onChange={(e) => setSFrom(e.target.value)} className="input !py-1.5 text-sm" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">{t('reconciliation.searchTo')}</span>
                    <input type="date" value={sTo} onChange={(e) => setSTo(e.target.value)} className="input !py-1.5 text-sm" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">{t('reconciliation.searchMin')}</span>
                    <input type="number" step="0.01" min="0" value={sMin} onChange={(e) => setSMin(e.target.value)} className="input !py-1.5 text-right text-sm" placeholder="0,00" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">{t('reconciliation.searchMax')}</span>
                    <input type="number" step="0.01" min="0" value={sMax} onChange={(e) => setSMax(e.target.value)} className="input !py-1.5 text-right text-sm" placeholder="0,00" />
                  </label>
                  <label className="col-span-2 block">
                    <span className="mb-1 block text-xs text-ink-muted">{t('reconciliation.searchLabel')}</span>
                    <input value={sQuery} onChange={(e) => setSQuery(e.target.value)} className="input !py-1.5 text-sm" placeholder={t('reconciliation.searchLabelPlaceholder')} />
                  </label>
                  {hasStmtSearch && (
                    <div className="col-span-2 flex justify-end">
                      <button onClick={resetStmtSearch} className="btn btn-ghost !px-3 !py-1 text-sm">{t('reconciliation.searchReset')}</button>
                    </div>
                  )}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th>{t('field.date')}</th>
                      <th>{t('field.label')}</th>
                      <th className="text-right">{t('field.amount')}</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmtFiltered.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-ink-muted">{t('reconciliation.noStatementLines')}</td></tr>
                    )}
                    {stmtFiltered.map((l) => {
                      const sel = selectedStatement.includes(l.id);
                      return (
                      <tr
                        key={l.id}
                        onClick={() => setSelectedStatement((v) => toggle(v, l.id))}
                        className={`cursor-pointer ${sel ? 'bg-gold-tint ring-1 ring-inset ring-gold/50' : 'hover:bg-surface-raised'}`}
                      >
                        <td className="w-8">
                          <span className={`grid h-4 w-4 place-items-center rounded border ${sel ? 'border-gold bg-gold text-brand-contrast' : 'border-line'}`}>
                            {sel && <Check className="h-3 w-3" />}
                          </span>
                        </td>
                        <td data-label={t('field.date')} className="text-ink-soft">{dateFmt(l.operationDate)}</td>
                        <td data-label={t('field.label')}>
                          <span className="inline-flex items-center gap-2">
                            {l.bankLabel}
                            {l.duplicate && (
                              <span className={badgeNeutral} title={t('reconciliation.duplicateHint')}>
                                <AlertTriangle className="mr-1 inline h-3 w-3" />
                                {t('reconciliation.duplicate')}
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label={t('field.amount')} className={`tnum text-right ${l.amount < 0 ? 'text-expense' : 'text-income'}`}>{euro(l.amount)}</td>
                        <td className="w-10 text-right">
                          <button
                            type="button"
                            className="btn btn-ghost !px-2 !py-1 text-expense"
                            title={t('reconciliation.deleteLine')}
                            aria-label={t('reconciliation.deleteLine')}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(t('reconciliation.deleteLineConfirm'))) {
                                deleteStatementLine.mutate(l.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {/* Matches (collapsible) */}
          <div className="card p-5">
            <button
              type="button"
              onClick={() => setMatchesOpen((v) => !v)}
              className="flex w-full items-center justify-between"
            >
              <h3 className="font-medium">
                {t('reconciliation.matches')}
                {d.matches.length > 0 && <span className={`ml-2 ${badgeIncome}`}>{d.matches.length}</span>}
              </h3>
              {matchesOpen ? <ChevronUp className="h-4 w-4 text-ink-muted" /> : <ChevronDown className="h-4 w-4 text-ink-muted" />}
            </button>
            {matchesOpen && (d.matches.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">{t('reconciliation.noMatches')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('reconciliation.bookEntry')}</th>
                      <th>{t('reconciliation.statementLine')}</th>
                      <th>{t('field.source')}</th>
                      <th className="text-right">{t('field.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.matches.map((m) => {
                      const books = m.entries;
                      const stmts = m.statements;
                      return (
                        <tr key={m.id}>
                          <td data-label={t('reconciliation.bookEntry')}>
                            {books.length === 0
                              ? '-'
                              : books.map((b) => (
                                  <div key={b.id}>{dateFmt(b.date)} · {b.label}</div>
                                ))}
                          </td>
                          <td data-label={t('reconciliation.statementLine')}>
                            {stmts.length === 0
                              ? '-'
                              : stmts.map((s) => (
                                  <div key={s.id}>{dateFmt(s.date)} · {s.label}</div>
                                ))}
                          </td>
                          <td data-label={t('field.source')}>
                            {m.aiGenerated ? (
                              <span className={m.confirmed ? badgeIncome : badgeNeutral}>
                                {m.confirmed ? t('reconciliation.confirmed') : t('reconciliation.proposed')}
                              </span>
                            ) : (
                              <span className={badgeIncome}>{t('reconciliation.manual')}</span>
                            )}
                          </td>
                          <td data-label={t('field.actions')} className="cell-actions text-right">
                            {m.aiGenerated && !m.confirmed && (
                              <button
                                className="btn btn-ghost !px-2 !py-1 text-income"
                                onClick={() => confirmMatch.mutate({ matchId: m.id, confirmed: true })}
                              >
                                <Check className="h-4 w-4" />
                                {t('reconciliation.validate')}
                              </button>
                            )}
                            <button
                              className="btn btn-ghost !px-2 !py-1 text-expense"
                              onClick={() => removeMatch.mutate(m.id)}
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
